// Phase 10.5: Spatial Event Trace Layer (Optimized Ring Buffer)
// Constant memory, high-precision timing, and session correlation.

export type TraceEvent = {
    time: number;
    timestamp: string;
    type: "AI_INTENT" | "SPATIAL_DECISION" | "CAMERA_DISPATCH" | "MAP_ACTION" | "SYSTEM_RECOVERY" | "NAV_TELEMETRY" | "MODE_SWITCH" | "CAMERA_LOADED" | "STATE_TRANSITION" | "MARKERS_RENDERED";
    label: string;
    details?: any;
    source: "AI" | "USER" | "SYNC" | "SYSTEM";
    correlationId?: string;
};

const TRACE_LIMIT = 100;
let traceBuffer = new Array(TRACE_LIMIT);
let traceIndex = 0;
let currentCorrelationId: string | null = null;

let currentlyTracing = false;

export const traceStore = {
    setCorrelationId(id: string) {
        currentCorrelationId = id;
    },

    getCorrelationId() {
        return currentCorrelationId;
    },

    add(event: Omit<TraceEvent, "time" | "timestamp" | "correlationId">) {
        if (currentlyTracing) return;
        currentlyTracing = true;

        try {
            const perfTime = performance.now();
            const wallTime = new Date().toLocaleTimeString('en-US', { hour12: false });
            
            const newEvent: TraceEvent = { 
                ...event, 
                time: perfTime,
                timestamp: wallTime,
                correlationId: currentCorrelationId || undefined
            } as TraceEvent;
            
            traceBuffer[traceIndex] = newEvent;
            traceIndex = (traceIndex + 1) % TRACE_LIMIT;

            // Proactive developer logging - silences verbose logs by default
            if (import.meta.env.VITE_ENABLE_VERBOSE_LOGS === 'true') {
                console.groupCollapsed(`%c[Trace][${wallTime}] %c${event.type}%c: ${event.label}`, 
                             'color: #8b5cf6; font-weight: bold;', 
                             'color: #fff; background: #8b5cf6; padding: 2px 4px; border-radius: 2px;', 
                             'color: #ddd;');
                console.debug("Source:", event.source);
                console.debug("Correlation ID:", currentCorrelationId);
                if (event.details) console.debug("Payload:", event.details);
                console.groupEnd();
            }
        } finally {
            currentlyTracing = false;
        }
    },
    getHistory() {
        // Return sorted by time, filtering out empty slots
        return traceBuffer
            .filter(e => e !== undefined)
            .sort((a, b) => a.time - b.time);
    },

    clear() {
        traceBuffer = new Array(TRACE_LIMIT);
        traceIndex = 0;
        currentCorrelationId = null;
        if (import.meta.env.VITE_ENABLE_VERBOSE_LOGS === 'true') {
            console.info("[Trace] Ring buffer cleared.");
        }
    }
};

if (typeof window !== 'undefined') {
    (window as any).WingmanTrace = traceStore;
}
